import { ListItemButton, ListItemIcon, Popover } from '@mui/material';
// @mui
import { alpha, styled } from '@mui/material/styles';

//
import type { NavItemProps } from '../types';

// config
import { ICON, NAV } from '../../../config-global';
// utils
import { bgBlur } from '../../../utils/cssStyles';

// ----------------------------------------------------------------------

type StyledItemProps = Omit<NavItemProps, 'item'>;

export const StyledItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'open',
})<StyledItemProps>(({ active, depth, disabled, open, theme }) => {
  const isLight = theme.palette.mode === 'light';

  const subItem = depth !== 1;

  const activeStyle = {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    color: theme.palette.primary.main,
    ...(!isLight && {
      color: theme.palette.primary.light,
    }),
  };

  const activeSubStyle = {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
  };

  const hoverStyle = {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  };

  return {
    '&:hover': hoverStyle,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    display: 'inline-flex',
    flexShrink: 0,
    height: NAV.H_DASHBOARD_ITEM_HORIZONTAL,
    padding: theme.spacing(0, 0.75),
    textTransform: 'capitalize',
    // Sub item
    ...(subItem && {
      margin: 0,
      paddingLeft: theme.spacing(1),
      paddingRight: 0,
      width: '100%',
    }),
    // Active item
    ...(active && {
      ...activeStyle,
      '&:hover': {
        ...activeStyle,
      },
    }),
    // Active sub item
    ...(subItem &&
      active && {
        ...activeSubStyle,
        '&:hover': {
          ...activeSubStyle,
        },
      }),
    // Open
    ...(open && !active && hoverStyle),
    // Disabled
    ...(disabled && {
      '&.Mui-disabled': {
        opacity: 0.64,
      },
    }),
  };
});

// ----------------------------------------------------------------------

export const StyledIcon = styled(ListItemIcon)({
  flexShrink: 0,
  height: ICON.NAV_ITEM_HORIZONTAL,
  marginRight: 8,
  width: ICON.NAV_ITEM_HORIZONTAL,
});

// ----------------------------------------------------------------------

export const StyledPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPopover-paper': {
    borderRadius: Number(theme.shape.borderRadius) * 1.5,
    boxShadow: theme.customShadows.dropdown,
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1),
    pointerEvents: 'auto',
    width: 160,
    ...bgBlur({ color: theme.palette.background.default }),
  },
  pointerEvents: 'none',
}));
